import {BaseCommand} from './base.command';
import {StyleChangeSnapshot} from '../interfaces/command.interface';
import {CytoscapeService} from '../../cy-services/cytoscape.service';
import {NodeService} from '../../../api/services/node.service';
import {MapStateService} from '../../../api/services/map-state.service';
import {firstValueFrom, of} from 'rxjs';
import {catchError, defaultIfEmpty} from 'rxjs/operators';
import {set} from 'lodash';
import {getPrimaryStyleName, UndoLabelMetadata, UndoObjectType} from '../undo-label.utils';

/**
 * UpdateStyleCommand handles style/property changes for nodes and links.
 * Includes: color, opacity, shape, border, font size, etc.
 */
export class UpdateStyleCommand extends BaseCommand<StyleChangeSnapshot[]> {
  constructor(
    private cyService: CytoscapeService,
    private nodeService: NodeService,
    changes: StyleChangeSnapshot[],
    description?: string,
    private mapStateService?: MapStateService
  ) {
    const count = changes.length;

    // Determine object type based on count and element types
    let objectType: UndoObjectType;
    if (count === 1) {
      objectType = changes[0].elementType === 'node' ? 'Node' : 'Link';
    } else {
      // Check if all same type or mixed
      const hasNodes = changes.some(c => c.elementType === 'node');
      const hasLinks = changes.some(c => c.elementType === 'link');
      if (hasNodes && hasLinks) {
        objectType = 'Selection';
      } else if (hasNodes) {
        objectType = count === 1 ? 'Node' : 'Selection';
      } else {
        objectType = count === 1 ? 'Link' : 'Selection';
      }
    }

    // Get the primary style name for the part - only consider actually changed properties
    const firstChange = changes[0];
    const actuallyChangedStyles: Record<string, unknown> = {};
    for (const key of Object.keys(firstChange.afterStyles)) {
      const rawBeforeVal = firstChange.beforeStyles[key];
      const rawAfterVal = firstChange.afterStyles[key];

      // Skip if before value was undefined/null AND after value is also empty
      // This filters out fill-in defaults while still tracking meaningful changes
      // (e.g., setting imageUrl from undefined to a valid URL should be tracked)
      if ((rawBeforeVal === undefined || rawBeforeVal === null || rawBeforeVal === '') &&
        (rawAfterVal === undefined || rawAfterVal === null || rawAfterVal === '')) {
        continue;
      }

      const beforeVal = JSON.stringify(rawBeforeVal);
      const afterVal = JSON.stringify(rawAfterVal);
      if (beforeVal !== afterVal) {
        actuallyChangedStyles[key] = rawAfterVal;
      }
    }
    const styleName = getPrimaryStyleName(
      actuallyChangedStyles,
      firstChange.elementType
    );

    const labelMetadata: UndoLabelMetadata = {
      object: objectType,
      verb: 'Change',
      part: styleName || 'Style',
      elementCount: objectType === 'Selection' ? count : undefined
    };

    super('update-style', description || 'Style Change', {before: changes, after: changes}, labelMetadata);
  }

  /**
   * Convert flattened style keys (e.g., "properties.scale") to nested object
   */
  private unflattenStyles(styles: Record<string, unknown>): Record<string, unknown> {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(styles)) {
      set(result, key, value);
    }
    return result;
  }

  /**
   * Update nested state-specific data for MapState-aware properties.
   * When viewing a MapState, properties like excludedFromState are read from
   * states[].link or states[].node, not from the root level.
   */
  private updateStateSpecificData(element: any, key: string, value: unknown, elementType: 'node' | 'link'): void {
    const currentState = this.mapStateService?.getCurrentLocalState();
    if (!currentState?.id) return;

    const states = element.data('states');
    if (!Array.isArray(states)) return;

    const stateEntry = states.find((s: any) => s.stateId === currentState.id);
    if (!stateEntry) return;

    const stateData = elementType === 'link' ? stateEntry.link : stateEntry.node;
    if (!stateData) return;

    stateData[key] = value;
    // Trigger Cytoscape to detect the change by setting a new array reference
    element.data('states', [...states]);
  }

  /**
   * Apply styles to cytoscape element, handling nested properties
   */
  private applyStylesToElement(element: any, styles: Record<string, unknown>, elementId: string): void {
    for (const [key, value] of Object.entries(styles)) {
      if (key.includes('.')) {
        // Nested property like "properties.scale"
        const [parent, child] = key.split('.');
        // Clone the parent object to avoid mutating by reference
        const parentData = {...(element.data(parent) || {})};
        parentData[child] = value;
        element.data(parent, parentData);
      } else {
        element.data(key, value);

        // For excludedFromState, also update the nested state-specific data
        // so that getLinkDataBasedOnState/getNodeDataBasedOnState returns the correct value
        if (key === 'excludedFromState') {
          this.updateStateSpecificData(element, key, value, element.isEdge() ? 'link' : 'node');
        }

        // Also update link label signals for visual rendering
        if (key === 'linkLabelOffset' && typeof value === 'number') {
          this.cyService.updateLinkLabelOffset(elementId, value);
        } else if (key === 'linkLabelPosition' && typeof value === 'number') {
          this.cyService.updateLinkLabelPosition(elementId, value);
        }
      }
    }
  }

  /**
   * Refresh edge editing handles when link type changes (for execute - clears existing data)
   */
  private refreshEdgeEditingHandles(edge: any, newType: string): void {
    const cy = this.cyService.cy;
    if (!cy || !(cy as any).edgeEditing) return;
    const edgeEditingInstance = (cy as any).edgeEditing('get');
    if (!edgeEditingInstance) return;

    // Clear existing edge editing data
    edge.data('controlPointPositions', null);
    edge.data('bendPointPositions', null);
    edge.data('cyedgebendeditingDistances', null);
    edge.data('cyedgebendeditingWeights', null);
    edge.data('cyedgecontroleditingWeights', null);
    edge.data('cyedgecontroleditingDistances', null);
    edge.removeClass('edgebendediting-hasbendpoints');
    edge.removeClass('edgecontrolediting-hascontrolpoints');

    // Set appropriate class based on new type
    if (newType === 'BEZIER') {
      edge.addClass('ignore_control_points');
    } else {
      edge.removeClass('ignore_control_points');
    }

    // Reinitialize anchor points
    const edgeEditingType = newType === 'BEZIER' ? 'none' : newType === 'STRAIGHT' ? 'bend' : 'control';
    edgeEditingInstance.initAnchorPoints([edge], edgeEditingType);
  }

  /**
   * Reinitialize edge editing handles after restoring anchor data (for undo - preserves existing data)
   */
  private reinitializeEdgeHandles(edge: any, newType: string): void {
    const cy = this.cyService.cy;
    if (!cy || !(cy as any).edgeEditing) return;
    const edgeEditingInstance = (cy as any).edgeEditing('get');
    if (!edgeEditingInstance) return;

    // Update classes based on new type
    edge.removeClass('edgebendediting-hasbendpoints');
    edge.removeClass('edgecontrolediting-hascontrolpoints');

    if (newType === 'BEZIER') {
      edge.addClass('ignore_control_points');
    } else {
      edge.removeClass('ignore_control_points');
    }

    // Check if we have existing anchor data to restore
    const controlPoints = edge.data('controlPointPositions');
    const bendPoints = edge.data('bendPointPositions');

    if (newType === 'UNBUNDLED_BEZIER' && controlPoints?.length > 0) {
      edge.addClass('edgecontrolediting-hascontrolpoints');
    } else if (newType === 'STRAIGHT' && bendPoints?.length > 0) {
      edge.addClass('edgebendediting-hasbendpoints');
    }

    // Reinitialize anchor points visualization
    const edgeEditingType = newType === 'BEZIER' ? 'none' : newType === 'STRAIGHT' ? 'bend' : 'control';
    edgeEditingInstance.initAnchorPoints([edge], edgeEditingType);
  }

  async execute(): Promise<void> {
    const cy = this.cyService.cy;
    if (!cy) return;

    const changes = this.snapshot.after;

    cy.batch(() => {
      changes.forEach(change => {
        const element = cy.$(`#${change.elementId}`);
        if (element.length === 0) return;

        // Apply "after" styles to cytoscape data
        this.applyStylesToElement(element, change.afterStyles, change.elementId);
      });
    });

    // Refresh edge editing handles if link type changed
    changes.forEach(change => {
      if (change.elementType === 'link' && 'type' in change.afterStyles) {
        const edge = cy.$(`#${change.elementId}`);
        if (edge.length > 0) {
          // Check if afterStyles contains anchor data (indicates redo operation)
          const hasAnchorData = change.afterStyles['controlPointPositions'] ||
              change.afterStyles['bendPointPositions'];
          if (hasAnchorData) {
            // Redo case: preserve the restored anchor data
            this.reinitializeEdgeHandles(edge, change.afterStyles['type'] as string);
          } else {
            // Initial execution: clear old data and reinitialize
            this.refreshEdgeEditingHandles(edge, change.afterStyles['type'] as string);
          }
        }
      }
    });

    // Update on backend
    const updatePromises = changes.map(change => {
      const unflattened = this.unflattenStyles(change.afterStyles);
      const payload = {
        id: change.elementId,
        ...unflattened
      };

      if (change.elementType === 'node') {
        return firstValueFrom(
          this.nodeService.updateNode(payload as any, true).pipe(
            defaultIfEmpty(null),
            catchError(err => {
              console.warn('UpdateStyleCommand: Failed to update node', change.elementId, err);
              return of(null);
            })
          )
        );
      } else {
        return firstValueFrom(
          this.nodeService.updateLink(payload as any).pipe(
            defaultIfEmpty(null),
            catchError(err => {
              console.warn('UpdateStyleCommand: Failed to update link', change.elementId, err);
              return of(null);
            })
          )
        );
      }
    });

    await Promise.all(updatePromises);

    cy.style().update();
    (cy as any).forceRender(); // Force canvas redraw for data-driven styles like label size

    // Clear animated opacity for elements with excludedFromState changes
    // This forces Cytoscape to re-evaluate the stylesheet's data-driven opacity
    changes.forEach(change => {
      if ('excludedFromState' in change.afterStyles) {
        const element = cy.$(`#${change.elementId}`);
        if (element.length > 0) {
          element.style('opacity', null);
        }
      }
    });
  }

  async undo(): Promise<void> {
    const cy = this.cyService.cy;
    if (!cy) return;

    const changes = this.snapshot.before;

    cy.batch(() => {
      changes.forEach(change => {
        const element = cy.$(`#${change.elementId}`);
        if (element.length === 0) return;

        // Restore "before" styles
        this.applyStylesToElement(element, change.beforeStyles, change.elementId);
      });
    });

    // Reinitialize edge editing handles if link type changed (preserving restored anchor data)
    changes.forEach(change => {
      if (change.elementType === 'link' && 'type' in change.beforeStyles) {
        const edge = cy.$(`#${change.elementId}`);
        if (edge.length > 0) {
          this.reinitializeEdgeHandles(edge, change.beforeStyles['type'] as string);
        }
      }
    });

    // Update on backend
    const updatePromises = changes.map(change => {
      const unflattened = this.unflattenStyles(change.beforeStyles);
      const payload = {
        id: change.elementId,
        ...unflattened
      };

      if (change.elementType === 'node') {
        return firstValueFrom(
          this.nodeService.updateNode(payload as any, true).pipe(
            defaultIfEmpty(null),
            catchError(err => {
              console.warn('UpdateStyleCommand: Failed to undo node update', change.elementId, err);
              return of(null);
            })
          )
        );
      } else {
        return firstValueFrom(
          this.nodeService.updateLink(payload as any).pipe(
            defaultIfEmpty(null),
            catchError(err => {
              console.warn('UpdateStyleCommand: Failed to undo link update', change.elementId, err);
              return of(null);
            })
          )
        );
      }
    });

    await Promise.all(updatePromises);

    cy.style().update();
    (cy as any).forceRender(); // Force canvas redraw for data-driven styles like label size

    // Clear animated opacity for elements with excludedFromState changes
    // This forces Cytoscape to re-evaluate the stylesheet's data-driven opacity
    changes.forEach(change => {
      if ('excludedFromState' in change.beforeStyles) {
        const element = cy.$(`#${change.elementId}`);
        if (element.length > 0) {
          element.style('opacity', null);
        }
      }
    });
  }
}
